/**
 * Three-year funnel model — pure math for the editable model on the 2027 Plan
 * tab (quick-260928-lh8, John 2026-09-28).
 *
 * Mirrors the "Three-year forecast" tab of the Shape of the Book artifact and
 * the seven metrics of the Funnel Metric Definitions & Benchmarks workbook:
 *
 *   inbound leads ─▶ booked ─▶ delivered ─┐
 *                                          ├─▶ opportunities ─▶ pipeline $ ─▶ wins ─▶ new ARR
 *   outbound conversations ─▶ booked ─▶ delivered ─┘        (every new-logo opp is meeting-sourced)
 *        ▲                                  │
 *        BDRs + AEs dial                    AEs work the meeting (capacity)
 *
 * WHO DOES WHAT (quick-261029-fkq, John 2026-10-29: "the bdrs will not close the deals but
 * they drive the funnel upward … the aes take the ball and the math is based on 5 after the
 * meeting is held"). Outbound conversations come from BDRs AND quota-carrying sellers (AEs),
 * each at their own measured rate. Delivered meetings are worked by the AEs, so the AE count
 * caps how many delivered meetings can become opportunities (meetingsPerAeMonth). Before this
 * change every BDR conversation was credited to the AEs, so "+1 seller" silently added a BDR's
 * worth of calls and "sellers needed" mixed the two roles.
 *
 * and, on top of the funnel, the existing base moving by expansion and churn.
 *
 * THE FUNNEL IS NEW LOGO ONLY (John 2026-09-28). Win rate, deal size and the
 * opportunity volume are New Business; expansion is entered separately as the
 * dollars expected from current customers per year, benchmarked against the
 * historical Rillet rate, so the total ARR forecast splits cleanly into new
 * logo and expansion.
 *
 * Two directions:
 *   - runModel()      forward: inputs → three projected years (the graph).
 *   - requiredChain() reverse: a target new-ARR figure → the pipeline, meetings,
 *                     leads, conversations and sellers it takes at the modeled
 *                     rates, next to what the modeled funnel actually produces.
 *                     This is the "how much pipe is needed, and can we create it"
 *                     question. Pipeline already in hand for the first plan year
 *                     (open plan-year New Business + 75% of the prior Q4's Best
 *                     Case + Pipeline, quick-261008-mi0) is netted off first, so
 *                     the chain is what is left to GENERATE.
 *
 * Conventions: rates 0..1; money USD; counts may be fractional (expected values).
 * No React, no IO — everything here is unit-testable.
 */

// Type-only: the "Likely" preset reads the Metric Trends monthly series the page hands it.
import type { FunnelTrends, TrendMonth } from './funnel-trends';

export interface Model3yInputs {
  /** Quota-carrying sellers (AEs) modeled (headcount). They work the delivered meetings and close. */
  sellers: number;
  /** Sellers on the books today — added sellers above this ramp in year 1. */
  baseSellers: number;
  /** Months for an added seller to reach full productivity. */
  rampMonths: number;
  /**
   * The AE's OWN outbound conversations (dialer dispositions that reached a human) per seller
   * per month. BDR conversations are NOT in here since quick-261029-fkq — see conversationsPerBdrMonth.
   */
  conversationsPerSellerMonth: number;
  /** BDRs modeled (headcount). They create conversations and book meetings; they do not close. */
  bdrs: number;
  /** BDRs on the books today — added BDRs above this ramp in year 1. */
  baseBdrs: number;
  /** Months for an added BDR to reach full productivity. */
  bdrRampMonths: number;
  /** Outbound conversations per BDR per month. */
  conversationsPerBdrMonth: number;
  /** BDRs hired at the start of each later year (year 2, year 3); each cohort ramps in its year. */
  bdrsAddedPerYear: number;
  /**
   * AE capacity: delivered new-logo meetings one fully ramped AE can work per month. Delivered
   * meetings above effective AEs × this × 12 go unworked (no opportunity). An assumption, not a
   * measurement: today's AEs run about 5 a month each.
   */
  meetingsPerAeMonth: number;
  /** Inbound leads per month (MAIN-27 basis). */
  inboundLeadsPerMonth: number;
  /** Inbound lead → booked new-business meeting (workbook M1). */
  leadToBooked: number;
  /** Inbound booked → delivered. */
  inboundDeliveredOfBooked: number;
  /** Inbound delivered meeting → opportunity (workbook M2, meeting basis). */
  inboundMtgToOpp: number;
  /** Outbound conversations per booked meeting (workbook M3). */
  conversationsPerMeeting: number;
  /** Outbound booked → delivered (delivered ÷ come-due). */
  outboundDeliveredOfBooked: number;
  /** Outbound delivered meeting → opportunity (workbook M4). */
  outboundMtgToOpp: number;
  /** Average created New Business opportunity ($) — pipeline per opp. */
  avgCreatedOppValue: number;
  /** New-logo win rate: New Business Closed Won ÷ (Closed Won + Closed Lost) (workbook M5). */
  winRate: number;
  /** Average won New Business deal ($) (workbook M7). */
  avgWonValue: number;
  /** Opportunity creation → close, DAYS (median; workbook M6 / agg_plan_cycle_timing). */
  cycleDays: number;
  /** Starting ARR ($) — Rillet. */
  startingArr: number;
  /**
   * Expansion ARR expected from CURRENT customers per year ($). Year 1 gets exactly
   * this amount; later years scale with the book (it is applied as a rate of the
   * opening balance, rate = amount ÷ starting ARR). Benchmark: the historical Rillet
   * expansion — set it above that to model an improvement.
   */
  expansionArrPerYear: number;
  /** Gross churn + contraction as a share of opening ARR per year — Rillet. */
  churnPct: number;
  /** Compounding annual change applied to win rate and deal size (0 = flat). */
  annualDrift: number;
  /**
   * GROWTH PER YEAR (John 2026-09-28: "the math shouldn't just give us the same
   * each year"). These make FY+1 and FY+2 differ from FY:
   */
  /** Inbound leads grow this much per year, compounding (0 = flat). */
  inboundGrowthPct: number;
  /** Sellers hired at the start of each later year (year 2, year 3); each cohort ramps in its year. */
  sellersAddedPerYear: number;
  /** Relative improvement per year, compounding, to lead→booked, meeting→opp and conversations per meeting (0 = flat). */
  conversionImprovementPct: number;
  /** Target new-logo ARR per year ($) — the reverse question. */
  targetNewArrPerYear: number;
  /**
   * PIPELINE IN HAND (quick-261008-mi0, John 2026-10-08: "ensure that the pipeline
   * for 2027 includes 75% of best case and pipeline from 2026 q4, and ensure that
   * the existing 2027 pipeline is accounted for in the math of what is needed to
   * generate"). Open New Business pipeline that already exists for the first plan
   * year; the required chain nets it against the pipeline the target needs.
   */
  /** Open New Business pipeline ($) already close-dated in the first plan year. */
  pipelineInHandOpen: number;
  /** Open New Business pipeline ($) close-dated in Q4 of the prior FY at Best Case or Pipeline. */
  pipelineInHandPriorQ4: number;
  /** Share of `pipelineInHandPriorQ4` assumed to roll into the plan year (0.75). */
  priorQ4RollForwardPct: number;
}

export interface Model3yYear {
  /** 0-based year index. */
  index: number;
  effectiveSellers: number;
  effectiveBdrs: number;
  inboundLeads: number;
  inboundBooked: number;
  inboundDelivered: number;
  inboundOpps: number;
  conversations: number;
  /** The BDRs' share of `conversations`. */
  bdrConversations: number;
  /** The AEs' own share of `conversations`. */
  sellerConversations: number;
  outboundBooked: number;
  /** Delivered outbound meetings the AEs worked (after the capacity cap). */
  outboundDelivered: number;
  outboundOpps: number;
  otherOpps: number;
  /** Delivered meetings (inbound + outbound) the AEs can work this year = effective AEs × meetingsPerAeMonth × 12. */
  aeMeetingCapacity: number;
  /** Delivered meetings that went unworked because they exceeded AE capacity (0 when under). */
  meetingsUnworked: number;
  /** Delivered meetings before the cap ÷ capacity (1 = fully loaded). */
  aeLoad: number;
  opps: number;
  /** Pipeline created $ = opps × avg created-opp value. */
  pipelineCreated: number;
  winRate: number;
  avgWonValue: number;
  wins: number;
  /** New-logo ARR produced by this year's wins (before cycle lag). */
  production: number;
  /** New-logo ARR recognized this year after the creation→close lag. */
  newArr: number;
  openingArr: number;
  expansion: number;
  churn: number;
  endingArr: number;
  growthPct: number;
  /** New-logo ARR growth vs the prior year (year 1 vs the carry-in run-rate); null when no prior. */
  newArrYoY: number | null;
}

export interface Model3yResult {
  years: Model3yYear[];
  endingArr: number;
  cagr: number;
  /** Reference year for the per-year comparisons = the first plan year (FY planYear). */
  steady: Model3yYear;
  /** Expansion − churn, as an NRR-style figure (1.10 = 110%). */
  nrr: number;
  /** Expansion rate applied to each year's opening balance (= expansionArrPerYear ÷ startingArr). */
  expansionPct: number;
  /** Three-year totals — the new-logo vs expansion split of the ARR forecast. */
  totals: {
    newLogo: number;
    expansion: number;
    churn: number;
    net: number;
    /** newLogo ÷ (newLogo + expansion), 0..1. */
    newLogoShareOfGross: number;
  };
}

export interface RequiredStage {
  key: string;
  label: string;
  unit: 'count' | 'money';
  /** Needed per year to hit the target at the modeled rates. */
  required: number;
  /** What the modeled funnel produces per year (steady state). */
  modeled: number;
}

export interface RequiredChain {
  target: number;
  stages: RequiredStage[];
  /** Pipeline $ the target needs in the reference year, in total (in hand + to generate). */
  requiredPipeline: number;
  /** requiredPipeline ÷ target. */
  coverageMultiple: number;
  /**
   * Pipeline already in hand for the reference year (quick-261008-mi0) =
   * open plan-year New Business + priorQ4RollForwardPct × prior-Q4 Best Case + Pipeline.
   */
  pipelineInHand: number;
  /** The open plan-year part of `pipelineInHand`. */
  pipelineInHandOpen: number;
  /** The rolled-forward prior-Q4 part of `pipelineInHand` (after the roll-forward share). */
  pipelineInHandPriorQ4Rolled: number;
  /** Opps the in-hand pipeline represents at the modeled average created-opp value. */
  pipelineInHandOpps: number;
  /** Expected new-logo ARR from the in-hand pipeline = pipelineInHand ÷ coverageMultiple. */
  pipelineInHandExpectedArr: number;
  /** requiredPipeline − pipelineInHand, floored at 0 — what the funnel still has to create. */
  pipelineToGenerate: number;
  /**
   * BDRs needed to create the outbound conversations still to generate, after the modeled AEs'
   * own conversations (quick-261029-fkq).
   */
  bdrsNeeded: number;
  /** AEs needed to work the delivered meetings still to generate, at meetingsPerAeMonth. */
  aesNeeded: number;
  /** Win rate that would hit the target with the modeled opportunity volume. */
  winRateNeeded: number;
  /** Average deal size that would hit the target with the modeled volume and win rate. */
  avgWonNeeded: number;
  /** Expected new-logo ARR per created opp = win rate × average won deal ($). */
  expectedArrPerOpp: number;
  /** (Modeled reference-year production + expected ARR from pipeline in hand) ÷ target (1 = on target). */
  attainment: number;
  /** Short verdict. */
  verdict: 'on-target' | 'gap-small' | 'gap-large';
}

const clamp01 = (x: number): number => Math.max(0, Math.min(1, x));
const safeDiv = (n: number, d: number): number => (d > 0 && Number.isFinite(n / d) ? n / d : 0);

/** Production of one year at the given inputs (pure funnel, no base). */
function produceYear(
  inp: Model3yInputs,
  index: number,
  effectiveSellers: number,
  effectiveBdrs: number,
): Omit<Model3yYear, 'newArr' | 'openingArr' | 'expansion' | 'churn' | 'endingArr' | 'growthPct' | 'newArrYoY'> {
  const drift = Math.pow(1 + inp.annualDrift, index);
  const conv = Math.pow(1 + Math.max(-0.99, inp.conversionImprovementPct), index); // conversion improvement, compounding
  const demand = Math.pow(1 + Math.max(-0.99, inp.inboundGrowthPct), index); // inbound growth, compounding
  // top of funnel: BDRs and AEs both dial, each at their own rate
  const bdrConversations = Math.max(0, effectiveBdrs) * Math.max(0, inp.conversationsPerBdrMonth) * 12;
  const sellerConversations = Math.max(0, effectiveSellers) * Math.max(0, inp.conversationsPerSellerMonth) * 12;
  const conversations = bdrConversations + sellerConversations;
  const outboundBooked = safeDiv(conversations, inp.conversationsPerMeeting / conv);
  const outboundHeld = outboundBooked * clamp01(inp.outboundDeliveredOfBooked);
  const inboundLeads = inp.inboundLeadsPerMonth * 12 * demand;
  const inboundBooked = inboundLeads * clamp01(inp.leadToBooked * conv);
  const inboundHeld = inboundBooked * clamp01(inp.inboundDeliveredOfBooked);
  // after the meeting: the AEs work it, and there are only so many they can take
  const aeMeetingCapacity = Math.max(0, effectiveSellers) * Math.max(0, inp.meetingsPerAeMonth) * 12;
  const held = inboundHeld + outboundHeld;
  const worked = held > aeMeetingCapacity ? safeDiv(aeMeetingCapacity, held) : 1; // both channels lose the same share
  const inboundDelivered = inboundHeld * worked;
  const outboundDelivered = outboundHeld * worked;
  const outboundOpps = outboundDelivered * clamp01(inp.outboundMtgToOpp * conv);
  const inboundOpps = inboundDelivered * clamp01(inp.inboundMtgToOpp * conv);
  const otherOpps = 0; // every new-logo opp is meeting-sourced (John 2026-09-28); kept at 0 for the shape
  const opps = inboundOpps + outboundOpps;
  const winRate = clamp01(inp.winRate * drift);
  const avgWonValue = Math.max(0, inp.avgWonValue * drift);
  const wins = opps * winRate;
  return {
    index,
    effectiveSellers,
    effectiveBdrs,
    inboundLeads,
    inboundBooked,
    inboundDelivered,
    inboundOpps,
    conversations,
    bdrConversations,
    sellerConversations,
    outboundBooked,
    outboundDelivered,
    outboundOpps,
    otherOpps,
    aeMeetingCapacity,
    meetingsUnworked: Math.max(0, held - aeMeetingCapacity),
    aeLoad: aeMeetingCapacity > 0 ? held / aeMeetingCapacity : 0,
    opps,
    pipelineCreated: opps * Math.max(0, inp.avgCreatedOppValue),
    winRate,
    avgWonValue,
    wins,
    production: wins * avgWonValue,
  };
}

/**
 * Forward model: three years.
 *
 * @param inp        the (edited) inputs
 * @param carryIn    production of the year BEFORE year 1 — the pipeline already
 *                   in flight. Pass the actuals' year-0 production so a scenario
 *                   that ramps still opens on today's run-rate. Defaults to the
 *                   model's own year-0 production (steady state).
 */
export function runModel(inp: Model3yInputs, carryIn?: number): Model3yResult {
  const lag = clamp01(inp.cycleDays / 365); // share of a year's production that closes next year
  const added = Math.max(0, inp.sellers - inp.baseSellers);
  const rampFactor = clamp01((12 - Math.max(0, inp.rampMonths)) / 12);
  const addedBdrs = Math.max(0, inp.bdrs - inp.baseBdrs);
  const bdrRampFactor = clamp01((12 - Math.max(0, inp.bdrRampMonths)) / 12);
  const bdrHires = Math.max(0, inp.bdrsAddedPerYear);
  const years: Model3yYear[] = [];
  const start = Math.max(0, inp.startingArr);
  const expansionArr = Math.max(0, inp.expansionArrPerYear);
  // Year 1 = the dollars entered; later years scale with the book.
  const expansionPct = start > 0 ? expansionArr / start : 0;
  let opening = start;
  let prevProduction = carryIn;
  let prevNewArr: number | null = carryIn ?? null;
  const hires = Math.max(0, inp.sellersAddedPerYear);
  for (let y = 0; y < 3; y++) {
    // year 1: today's sellers + year-1 additions ramping; later years: everyone hired so far
    // at full productivity + this year's cohort ramping.
    const effectiveSellers =
      y === 0 ? Math.min(inp.sellers, inp.baseSellers) + added * rampFactor : inp.sellers + hires * (y - 1) + hires * rampFactor;
    const effectiveBdrs =
      y === 0
        ? Math.min(inp.bdrs, inp.baseBdrs) + addedBdrs * bdrRampFactor
        : inp.bdrs + bdrHires * (y - 1) + bdrHires * bdrRampFactor;
    const p = produceYear(inp, y, effectiveSellers, effectiveBdrs);
    const prior = prevProduction ?? p.production;
    const newArr = p.production * (1 - lag) + prior * lag;
    const expansion = start > 0 ? opening * expansionPct : expansionArr;
    const churn = opening * Math.max(0, inp.churnPct);
    const endingArr = opening + newArr + expansion - churn;
    years.push({
      ...p,
      newArr,
      openingArr: opening,
      expansion,
      churn,
      endingArr,
      growthPct: safeDiv(endingArr - opening, opening),
      newArrYoY: prevNewArr != null && prevNewArr > 0 ? newArr / prevNewArr - 1 : null,
    });
    prevProduction = p.production;
    prevNewArr = newArr;
    opening = endingArr;
  }
  // eslint-disable-next-line @typescript-eslint/non-nullable-type-assertion-style -- runModel always builds 3 years; the rule's `!` fix is rejected by no-non-null-assertion in src
  const last = years[2] as Model3yYear;
  // eslint-disable-next-line @typescript-eslint/non-nullable-type-assertion-style -- runModel always builds 3 years; the rule's `!` fix is rejected by no-non-null-assertion in src
  const steady = years[0] as Model3yYear; // reference year = the first plan year
  const cagr = start > 0 && last.endingArr > 0 ? Math.pow(last.endingArr / start, 1 / 3) - 1 : 0;
  const sum = (f: (y: Model3yYear) => number): number => years.reduce((t, y) => t + f(y), 0);
  const newLogo = sum((y) => y.newArr);
  const expansion = sum((y) => y.expansion);
  const churn = sum((y) => y.churn);
  return {
    years,
    endingArr: last.endingArr,
    cagr,
    steady,
    nrr: 1 + expansionPct - Math.max(0, inp.churnPct),
    expansionPct,
    totals: { newLogo, expansion, churn, net: newLogo + expansion - churn, newLogoShareOfGross: safeDiv(newLogo, newLogo + expansion) },
  };
}

// ---- pipeline in hand (quick-261008-mi0, John 2026-10-08) ------------------------
//
// "ensure that the pipeline for 2027 includes 75% of best case and pipeline from
// 2026 q4, and ensure that the existing 2027 pipeline is accounted for in the math
// of what is needed to generate." Two slices of open New Business Product pipeline
// are counted as already in hand for the first plan year:
//   1. opps close-dated anywhere in the plan year (they exist today), and
//   2. PRIOR_Q4_ROLL_FORWARD_PCT of the prior Q4's opps at the two weakest forecast
//      positions (Best Case, Pipeline) — most of those will not close in Q4 and
//      roll into the plan year. Commit / Most Likely stay in the prior year.
// The required chain nets the sum against the pipeline the target needs, so every
// upstream stage (opps, meetings, leads, conversations, sellers) is what is left
// to GENERATE, and attainment credits the in-hand pipeline's expected yield.

/** Share of the prior Q4's Best Case + Pipeline assumed to roll into the plan year. */
export const PRIOR_Q4_ROLL_FORWARD_PCT = 0.75;

/** The Forecast_Position__c values that roll forward (the weakest two). */
export const PRIOR_Q4_ROLL_FORWARD_POSITIONS: readonly string[] = ['Best Case', 'Pipeline'];

/** Sum the rolling-forward positions out of a prior-Q4 by-position map ($ and opps). */
export function priorQ4RollForwardBase(
  byPosition: Readonly<Record<string, { amount: number; oppCount: number }>>,
): { amount: number; oppCount: number } {
  let amount = 0;
  let oppCount = 0;
  for (const pos of PRIOR_Q4_ROLL_FORWARD_POSITIONS) {
    const cell = byPosition[pos];
    if (!cell) continue;
    amount += Math.max(0, cell.amount);
    oppCount += Math.max(0, cell.oppCount);
  }
  return { amount, oppCount };
}

/** Pipeline in hand = open plan-year + roll-forward share × prior-Q4 Best Case + Pipeline. */
export function pipelineInHand(inp: Pick<Model3yInputs, 'pipelineInHandOpen' | 'pipelineInHandPriorQ4' | 'priorQ4RollForwardPct'>): {
  open: number;
  priorQ4Rolled: number;
  total: number;
} {
  const open = Math.max(0, inp.pipelineInHandOpen);
  const priorQ4Rolled = Math.max(0, inp.pipelineInHandPriorQ4) * clamp01(inp.priorQ4RollForwardPct);
  return { open, priorQ4Rolled, total: open + priorQ4Rolled };
}

/**
 * Reverse model: what the funnel must produce per year to hit `targetNewArrPerYear`
 * at the modeled rates, next to what it does produce in the reference year (the
 * first plan year). Inbound/outbound split follows the modeled mix of
 * funnel-sourced opps; every new-logo opp is meeting-sourced. Pipeline already in
 * hand (quick-261008-mi0) is subtracted first: the opps/meetings/leads/
 * conversations/sellers rows are what is left to generate.
 */
export function requiredChain(inp: Model3yInputs, result: Model3yResult): RequiredChain {
  const target = Math.max(0, inp.targetNewArrPerYear);
  const s = result.steady;
  const avgCreated = Math.max(0, inp.avgCreatedOppValue);
  const reqWins = safeDiv(target, s.avgWonValue);
  const reqOppsTotal = safeDiv(reqWins, s.winRate);
  const requiredPipeline = reqOppsTotal * avgCreated;
  // pipeline in hand → its opps, its expected ARR, and what is left to generate
  const inHand = pipelineInHand(inp);
  const inHandOpps = safeDiv(inHand.total, avgCreated);
  const inHandExpectedArr = inHandOpps * s.winRate * s.avgWonValue;
  const pipelineToGenerate = Math.max(0, requiredPipeline - inHand.total);
  const reqOpps = safeDiv(pipelineToGenerate, avgCreated);
  const funnelOpps = s.inboundOpps + s.outboundOpps;
  const inboundShare = funnelOpps > 0 ? s.inboundOpps / funnelOpps : 0.5;
  const reqFunnelOpps = Math.max(0, reqOpps - s.otherOpps);
  const reqInOpps = reqFunnelOpps * inboundShare;
  const reqObOpps = reqFunnelOpps * (1 - inboundShare);
  const reqInDelivered = safeDiv(reqInOpps, clamp01(inp.inboundMtgToOpp));
  const reqInBooked = safeDiv(reqInDelivered, clamp01(inp.inboundDeliveredOfBooked));
  const reqLeads = safeDiv(reqInBooked, clamp01(inp.leadToBooked));
  const reqObDelivered = safeDiv(reqObOpps, clamp01(inp.outboundMtgToOpp));
  const reqObBooked = safeDiv(reqObDelivered, clamp01(inp.outboundDeliveredOfBooked));
  const reqConversations = reqObBooked * Math.max(0, inp.conversationsPerMeeting);
  // the modeled AEs' own dials count first; BDRs make the rest
  const bdrsNeeded = safeDiv(Math.max(0, reqConversations - s.sellerConversations), inp.conversationsPerBdrMonth * 12);
  const aesNeeded = safeDiv(reqInDelivered + reqObDelivered, inp.meetingsPerAeMonth * 12);
  // single-lever fixes count the in-hand opps alongside the funnel's
  const allOpps = s.opps + inHandOpps;
  const winRateNeeded = safeDiv(reqWins, allOpps);
  const avgWonNeeded = safeDiv(target, allOpps * s.winRate);
  const attainment = safeDiv(s.production + inHandExpectedArr, target);
  const stages: RequiredStage[] = [
    { key: 'leads', label: 'Inbound leads', unit: 'count', required: reqLeads, modeled: s.inboundLeads },
    { key: 'inBooked', label: 'Inbound meetings booked', unit: 'count', required: reqInBooked, modeled: s.inboundBooked },
    { key: 'inDelivered', label: 'Inbound meetings delivered', unit: 'count', required: reqInDelivered, modeled: s.inboundDelivered },
    { key: 'conversations', label: 'Outbound conversations', unit: 'count', required: reqConversations, modeled: s.conversations },
    { key: 'obBooked', label: 'Outbound meetings booked', unit: 'count', required: reqObBooked, modeled: s.outboundBooked },
    { key: 'obDelivered', label: 'Outbound meetings delivered', unit: 'count', required: reqObDelivered, modeled: s.outboundDelivered },
    { key: 'opps', label: 'Opportunities to create', unit: 'count', required: reqOpps, modeled: s.opps },
    { key: 'pipeline', label: 'Pipeline to generate', unit: 'money', required: pipelineToGenerate, modeled: s.pipelineCreated },
    { key: 'pipelineTotal', label: 'Pipeline, total (in hand + created)', unit: 'money', required: requiredPipeline, modeled: inHand.total + s.pipelineCreated },
    { key: 'wins', label: 'Wins', unit: 'count', required: reqWins, modeled: s.wins + inHandOpps * s.winRate },
    { key: 'newArr', label: 'New-logo ARR', unit: 'money', required: target, modeled: s.production + inHandExpectedArr },
  ];
  const verdict: RequiredChain['verdict'] = attainment >= 0.98 ? 'on-target' : attainment >= 0.8 ? 'gap-small' : 'gap-large';
  return {
    target,
    stages,
    requiredPipeline,
    // coverage = pipeline per $ of ARR = avg created opp ÷ (win rate × avg won deal)
    coverageMultiple: safeDiv(requiredPipeline, target),
    pipelineInHand: inHand.total,
    pipelineInHandOpen: inHand.open,
    pipelineInHandPriorQ4Rolled: inHand.priorQ4Rolled,
    pipelineInHandOpps: inHandOpps,
    pipelineInHandExpectedArr: inHandExpectedArr,
    pipelineToGenerate,
    expectedArrPerOpp: s.winRate * s.avgWonValue,
    bdrsNeeded,
    aesNeeded,
    winRateNeeded,
    avgWonNeeded,
    attainment,
    verdict,
  };
}

// ---- scenario presets -------------------------------------------------------

export type Model3yPresetId =
  | 'actuals'
  | 'likely'
  | 'plan-growth'
  | 'hire-more'
  | 'hire-bdrs'
  | 'hire-none-improve'
  | 'improve-across'
  | 'outbound-benchmark'
  | 'plan-stretch'
  | 'downturn';

/** The preset the page opens on: actual rates plus the planned growth levers, solved to PLAN_ARR_GROWTH. */
export const MODEL3Y_DEFAULT_PRESET: Model3yPresetId = 'plan-growth';

/**
 * What a preset may read besides the actuals (quick-261007-ffh): the carry-in the
 * page runs the model with, the Metric Trends monthly series (for "Likely"), and
 * the meeting→opp trace scale (so trend rates are scaled the same way the actuals are).
 */
export interface PresetContext {
  carryIn?: number;
  trends?: FunnelTrends;
  mtgToOppScale?: number;
}

export interface Model3yPreset {
  id: Model3yPresetId;
  label: string;
  description: string;
  apply: (actuals: Model3yInputs, ctx?: PresetContext) => Model3yInputs;
  /** Optional data-dependent one-liner (what the solve or the pooling actually used) shown with the preset. */
  describe?: (actuals: Model3yInputs, ctx?: PresetContext) => string;
}

const pts = (rate: number, delta: number): number => clamp01(rate + delta);

export const MODEL3Y_PRESETS: Model3yPreset[] = [
  {
    id: 'actuals',
    label: 'Actuals (no growth)',
    description: "Today's measured funnel, unchanged and flat: no hires, no inbound growth, no conversion improvement. Every year repeats.",
    apply: (a) => ({ ...a }),
  },
  {
    id: 'likely',
    label: 'Likely (recent run rate)',
    description:
      'How the trend is shaping up: every measurable input set to its pooled value over the two most recent months with enough data (Metric Trends), flat from there — no hires, no growth levers.',
    apply: (a, ctx) => (ctx?.trends ? buildLikely(a, ctx.trends, ctx.mtgToOppScale ?? 1) : { ...a }),
    describe: (_a, ctx) => (ctx?.trends ? likelyBasisText(ctx.trends) : 'Metric Trends series not loaded — same as actuals'),
  },
  {
    id: 'plan-growth',
    label: 'Plan growth (22% ARR)',
    description:
      'The plan shape: +2 AEs now and +1 each later year, inbound +10%/yr, conversion +5%/yr, win rate and deal size +3%/yr, 12 conversations per booked meeting, expansion +15% over historical — and the new-logo win rate solved so ARR compounds at 22% a year over the three years (capped at the 22% benchmark ceiling; BDR hires fill any remainder, plus AEs if the meetings outgrow them).',
    apply: (a, ctx) => solvePlanGrowth(a, ctx?.carryIn).inputs,
    describe: (a, ctx) => {
      const s = solvePlanGrowth(a, ctx?.carryIn);
      const extra: string[] = [];
      if (s.extraBdrs > 0) extra.push(`+${String(s.extraBdrs)} BDRs`);
      if (s.extraSellers > 0) extra.push(`+${String(s.extraSellers)} more AEs`);
      const hires = extra.length ? ` and ${extra.join(' and ')} now` : '';
      return `win rate solved to ${(s.inputs.winRate * 100).toFixed(1)}%${hires} → ${(s.cagr * 100).toFixed(1)}% compound ARR growth`;
    },
  },
  {
    id: 'hire-more',
    label: 'Hire 3 AEs now, +1 a year, same performance',
    description: 'Three added AEs in year 1 and one more each later year, on today’s conversion rates; each cohort ramps in its year. AEs add their own dials and meeting capacity, not BDR volume.',
    apply: (a) => ({ ...a, sellers: a.baseSellers + 3, sellersAddedPerYear: 1 }),
  },
  {
    id: 'hire-bdrs',
    label: 'Hire 2 BDRs now, +1 a year, same performance',
    description: 'Two added BDRs in year 1 and one more each later year, on today’s conversion rates and today’s AEs; each cohort ramps in its year.',
    apply: (a) => ({ ...a, bdrs: a.baseBdrs + 2, bdrsAddedPerYear: 1 }),
  },
  {
    id: 'hire-none-improve',
    label: 'Hire none, improve conversion',
    description: 'Same team; win rate +5 pts, meeting→opp +5 pts, 25% fewer conversations per meeting in year 1, then conversion +5%/yr.',
    apply: (a) => ({
      ...a,
      sellers: a.baseSellers,
      sellersAddedPerYear: 0,
      bdrs: a.baseBdrs,
      bdrsAddedPerYear: 0,
      winRate: pts(a.winRate, 0.05),
      inboundMtgToOpp: pts(a.inboundMtgToOpp, 0.05),
      outboundMtgToOpp: pts(a.outboundMtgToOpp, 0.05),
      conversationsPerMeeting: a.conversationsPerMeeting * 0.75,
      conversionImprovementPct: 0.05,
    }),
  },
  {
    id: 'improve-across',
    label: 'Improvements across the board',
    description: '+2 sellers now and +1 a year, win rate +3 pts, meeting→opp +3 pts, deal size +10%, inbound +10%/yr, conversion +5%/yr, churn −1 pt, expansion +20% over historical.',
    apply: (a) => ({
      ...a,
      sellers: a.baseSellers + 2,
      sellersAddedPerYear: 1,
      winRate: pts(a.winRate, 0.03),
      inboundMtgToOpp: pts(a.inboundMtgToOpp, 0.03),
      outboundMtgToOpp: pts(a.outboundMtgToOpp, 0.03),
      conversationsPerMeeting: a.conversationsPerMeeting * 0.85,
      avgWonValue: a.avgWonValue * 1.1,
      inboundGrowthPct: 0.1,
      conversionImprovementPct: 0.05,
      churnPct: Math.max(0, a.churnPct - 0.01),
      expansionArrPerYear: a.expansionArrPerYear * 1.2,
    }),
  },
  {
    id: 'outbound-benchmark',
    label: 'Hit the outbound benchmark',
    description: 'Seven conversations per booked meeting (Bridge Group benchmark) instead of today’s; nothing else changes.',
    apply: (a) => ({ ...a, conversationsPerMeeting: Math.min(a.conversationsPerMeeting, 7) }),
  },
  {
    id: 'plan-stretch',
    label: 'Plan stretch',
    description: 'The 2027 plan’s stretch shape on a new-logo basis: +3 sellers now and +2 a year, inbound +30% then +20%/yr, meeting→opp +10% relative then +5%/yr, win rate +5 pts, deal size +10%, expansion +25% over historical.',
    apply: (a) => ({
      ...a,
      sellers: a.baseSellers + 3,
      sellersAddedPerYear: 2,
      inboundLeadsPerMonth: a.inboundLeadsPerMonth * 1.3,
      inboundGrowthPct: 0.2,
      inboundMtgToOpp: clamp01(a.inboundMtgToOpp * 1.1),
      outboundMtgToOpp: clamp01(a.outboundMtgToOpp * 1.1),
      conversionImprovementPct: 0.05,
      winRate: pts(a.winRate, 0.05),
      avgWonValue: a.avgWonValue * 1.1,
      expansionArrPerYear: a.expansionArrPerYear * 1.25,
    }),
  },
  {
    id: 'downturn',
    label: 'Downturn',
    description: 'Inbound −25% then −10%/yr, win rate −3 pts, deal size −10%, churn +5 pts, expansion halved, no hires.',
    apply: (a) => ({
      ...a,
      sellersAddedPerYear: 0,
      bdrsAddedPerYear: 0,
      inboundLeadsPerMonth: a.inboundLeadsPerMonth * 0.75,
      inboundGrowthPct: -0.1,
      winRate: pts(a.winRate, -0.03),
      avgWonValue: a.avgWonValue * 0.9,
      churnPct: a.churnPct + 0.05,
      expansionArrPerYear: a.expansionArrPerYear * 0.5,
    }),
  },
];

// ---- "Likely": the recent run rate (quick-261007-ffh, John 2026-10-07) --------------
//
// "a likely scenario based on how the trends are currently going … current run rate
// averaged from recent two months of data". Each measurable input is pooled (sum of
// numerators ÷ sum of denominators) over the two most recent months of the Metric
// Trends series that are complete and mature; when those two months carry too few
// records for the figure to mean anything, earlier months are added one at a time
// until the pooled n clears the floor. If the whole series is still too thin the
// input keeps its actual. Volumes are the plain mean of the last two months.

/** Minimum pooled denominator for a rate (meetings / leads / decided deals) and for an average (deals). */
export const LIKELY_MIN_N = { rate: 10, average: 5 } as const;

type TrendKey = Exclude<keyof TrendMonth, 'month' | 'partialMonth'>;

export interface PooledPoint {
  value: number;
  num: number;
  den: number;
  /** Months pooled, newest first, e.g. ["2026-09", "2026-08"]. */
  months: string[];
}

/**
 * Pool the two most recent usable months of one series; walk back a month at a time
 * while the pooled denominator is under `minDen`. Null when the series cannot reach it.
 */
export function pooledRecent(trends: FunnelTrends, key: TrendKey, minDen: number): PooledPoint | null {
  const usable = trends.series.filter((m) => !m.partialMonth && !m[key].immature && m[key].value != null && m[key].den > 0);
  if (usable.length === 0) return null;
  let num = 0;
  let den = 0;
  const months: string[] = [];
  for (let j = usable.length - 1; j >= 0; j--) {
    const taken = usable.length - 1 - j; // how many months already pooled
    if (taken >= 2 && den >= minDen) break;
    const m = usable[j];
    if (!m) break;
    const p = m[key];
    num += p.num;
    den += p.den;
    months.push(m.month);
  }
  if (den < minDen || den <= 0) return null;
  return { value: num / den, num, den, months };
}

/** Which model input each trend series sets, and the n floor it must clear. */
const LIKELY_MAP: { input: keyof Model3yInputs; key: TrendKey; minDen: number; scaled?: boolean }[] = [
  { input: 'conversationsPerSellerMonth', key: 'conversationsPerSeller', minDen: 1 },
  { input: 'conversationsPerBdrMonth', key: 'conversationsPerBdr', minDen: 1 },
  { input: 'inboundLeadsPerMonth', key: 'inboundLeadsPerMonth', minDen: 1 },
  { input: 'leadToBooked', key: 'leadToBooked', minDen: LIKELY_MIN_N.rate },
  { input: 'inboundDeliveredOfBooked', key: 'inboundDeliveredOfBooked', minDen: LIKELY_MIN_N.rate },
  { input: 'inboundMtgToOpp', key: 'inboundMtgToOpp', minDen: LIKELY_MIN_N.rate, scaled: true },
  { input: 'conversationsPerMeeting', key: 'conversationsPerMeeting', minDen: LIKELY_MIN_N.rate },
  { input: 'outboundDeliveredOfBooked', key: 'outboundDeliveredOfDue', minDen: LIKELY_MIN_N.rate },
  { input: 'outboundMtgToOpp', key: 'outboundMtgToOpp', minDen: LIKELY_MIN_N.rate, scaled: true },
  { input: 'avgCreatedOppValue', key: 'avgCreatedOppValue', minDen: LIKELY_MIN_N.average },
  { input: 'winRate', key: 'newLogoWinRate', minDen: LIKELY_MIN_N.rate },
  { input: 'avgWonValue', key: 'avgWonValue', minDen: LIKELY_MIN_N.average },
  { input: 'cycleDays', key: 'cycleDays', minDen: LIKELY_MIN_N.average },
];

export interface LikelyBasisRow {
  input: keyof Model3yInputs;
  /** Null = the series was too thin, the actual is kept. */
  pooled: PooledPoint | null;
}

/** Per input: which months the Likely preset pooled (null = kept the actual). */
export function likelyBasis(trends: FunnelTrends): LikelyBasisRow[] {
  return LIKELY_MAP.map((m) => ({ input: m.input, pooled: pooledRecent(trends, m.key, m.minDen) }));
}

/**
 * The Likely input set: actuals with every measurable input replaced by its recent
 * pooled value. Meeting→opp rates carry the same trace scale as the actuals so the
 * two are comparable. Volumes per seller/month and leads/month are the two-month mean.
 * Growth levers stay at the measured trend (0) and expansion at historical.
 */
export function buildLikely(actuals: Model3yInputs, trends: FunnelTrends, mtgToOppScale = 1): Model3yInputs {
  const out: Model3yInputs = {
    ...actuals,
    sellers: actuals.baseSellers,
    sellersAddedPerYear: 0,
    bdrs: actuals.baseBdrs,
    bdrsAddedPerYear: 0,
    inboundGrowthPct: 0,
    conversionImprovementPct: 0,
    annualDrift: 0,
  };
  for (const m of LIKELY_MAP) {
    const p = pooledRecent(trends, m.key, m.minDen);
    if (!p) continue;
    // pooled num ÷ den is already the input's unit: conversations ÷ (sellers × months),
    // leads ÷ months, booked ÷ leads, $ ÷ deals, days ÷ deals …
    const v = m.scaled ? clamp01(p.value * mtgToOppScale) : p.value;
    out[m.input] = Math.max(0, v);
  }
  return out;
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthName = (ym: string): string => MONTHS_SHORT[Number(ym.slice(5, 7)) - 1] ?? ym;

/** One line: "Aug–Sep 2026 run rate pooled · widened for n: inbound mtg→opp 3 mo · kept at actuals (too few records): won deal $, cycle". */
export function likelyBasisText(trends: FunnelTrends): string {
  const rows = likelyBasis(trends);
  const latest = trends.series.filter((m) => !m.partialMonth).slice(-2);
  const first = latest[0];
  const last = latest[latest.length - 1];
  const span = latest.length === 2 && first && last ? `${monthName(first.month)}–${monthName(last.month)} ${last.month.slice(0, 4)}` : 'the latest months';
  const name = (k: keyof Model3yInputs): string => INPUT_SHORT[k] ?? k;
  const kept = rows.filter((r) => r.pooled == null).map((r) => name(r.input));
  const widened = rows.filter((r) => r.pooled != null && r.pooled.months.length > 2).map((r) => `${name(r.input)} ${String(r.pooled?.months.length ?? 0)} mo`);
  const parts = [`${span} run rate pooled`];
  if (widened.length) parts.push(`widened for n: ${widened.join(', ')}`);
  if (kept.length) parts.push(`kept at actuals (too few records): ${kept.join(', ')}`);
  return parts.join(' · ');
}

const INPUT_SHORT: Partial<Record<keyof Model3yInputs, string>> = {
  conversationsPerSellerMonth: 'conversations/AE',
  conversationsPerBdrMonth: 'conversations/BDR',
  inboundLeadsPerMonth: 'inbound leads',
  leadToBooked: 'lead→booked',
  inboundDeliveredOfBooked: 'inbound show',
  inboundMtgToOpp: 'inbound mtg→opp',
  conversationsPerMeeting: 'conv/meeting',
  outboundDeliveredOfBooked: 'outbound show',
  outboundMtgToOpp: 'outbound mtg→opp',
  avgCreatedOppValue: 'created opp $',
  winRate: 'win rate',
  avgWonValue: 'won deal $',
  cycleDays: 'cycle',
};

// ---- Plan growth solved to 22% ARR (quick-261007-ffh, John 2026-10-07) ------------
//
// "make the planned growth one to get 22% increase in arr". The plan shape is fixed
// (hires, inbound growth, conversion improvement, drift, outbound efficiency,
// expansion); the new-logo win rate is the balancing item, solved by bisection so
// three-year compound ARR growth lands on PLAN_ARR_GROWTH. The win rate is capped at
// the benchmark ceiling (LEVER_LIMITS.winRateCeiling); if that is not enough, whole
// sellers are added now until it is.

export const PLAN_ARR_GROWTH = 0.22;

/**
 * The target the "how much pipe does the new-logo target need" box opens on
 * (quick-261007-g7r, John 2026-10-07: "the pre-lim new logo target is 2.1M for 2027").
 * Preliminary — replace the amount when the plan is ratified; the page prints `basis`.
 */
export const PLAN2027_NEW_LOGO_TARGET = {
  amount: 2_100_000,
  basis: 'preliminary 2027 new-logo target (2026-10-07)',
} as const;

export const PLAN_GROWTH_SHAPE = {
  sellersNow: 2,
  sellersAddedPerYear: 1,
  inboundGrowthPct: 0.1,
  conversionImprovementPct: 0.05,
  annualDrift: 0.03,
  conversationsPerMeeting: 12,
  expansionOverHistorical: 1.15,
  maxExtraSellers: 30,
} as const;

export interface PlanGrowthSolve {
  inputs: Model3yInputs;
  /**
   * BDRs added now because the capped win rate was not enough (quick-261029-fkq: the funnel
   * lever is BDR volume, so the solver fills with BDRs, not AEs).
   */
  extraBdrs: number;
  /** AEs added now on top of the shape's +2 so the delivered meetings never exceed AE capacity. */
  extraSellers: number;
  cagr: number;
  /** True when PLAN_ARR_GROWTH could not be reached even at the caps. */
  short: boolean;
}

/** The fewest extra AEs (up to `max`) that leave no delivered meeting unworked in any of the three years. */
function aesToCoverMeetings(inp: Model3yInputs, carryIn: number | undefined, max: number): number {
  for (let extra = 0; extra < max; extra++) {
    const r = runModel({ ...inp, sellers: inp.sellers + extra }, carryIn);
    if (r.years.every((y) => y.meetingsUnworked <= 1e-9)) return extra;
  }
  return max;
}

function planShape(a: Model3yInputs, extraBdrs: number): Model3yInputs {
  const S = PLAN_GROWTH_SHAPE;
  return {
    ...a,
    sellers: a.baseSellers + S.sellersNow,
    sellersAddedPerYear: S.sellersAddedPerYear,
    bdrs: a.baseBdrs + extraBdrs,
    inboundGrowthPct: S.inboundGrowthPct,
    conversionImprovementPct: S.conversionImprovementPct,
    annualDrift: S.annualDrift,
    conversationsPerMeeting: Math.min(a.conversationsPerMeeting, S.conversationsPerMeeting),
    expansionArrPerYear: a.expansionArrPerYear * S.expansionOverHistorical,
  };
}

export function solvePlanGrowth(a: Model3yInputs, carryIn?: number, target = PLAN_ARR_GROWTH): PlanGrowthSolve {
  const ceiling = Math.max(a.winRate, LEVER_LIMITS.winRateCeiling);
  const cagrAt = (inp: Model3yInputs): number => runModel(inp, carryIn).cagr;
  const max = PLAN_GROWTH_SHAPE.maxExtraSellers;
  // shape + extra BDRs, then enough AEs to take every delivered meeting (win rate does not change meeting volume)
  const staffed = (extraBdrs: number): { base: Model3yInputs; extraSellers: number } => {
    const shaped = planShape(a, extraBdrs);
    const extraSellers = aesToCoverMeetings(shaped, carryIn, max);
    return { base: { ...shaped, sellers: shaped.sellers + extraSellers }, extraSellers };
  };
  for (let extra = 0; extra <= max; extra++) {
    const { base, extraSellers } = staffed(extra);
    const atFloor = cagrAt({ ...base, winRate: a.winRate });
    if (atFloor >= target) return { inputs: { ...base, winRate: a.winRate }, extraBdrs: extra, extraSellers, cagr: atFloor, short: false };
    const atCeiling = cagrAt({ ...base, winRate: ceiling });
    if (atCeiling >= target) {
      let lo = a.winRate;
      let hi = ceiling;
      for (let i = 0; i < 40; i++) {
        const mid = (lo + hi) / 2;
        if (cagrAt({ ...base, winRate: mid }) >= target) hi = mid;
        else lo = mid;
      }
      const inputs = { ...base, winRate: hi };
      return { inputs, extraBdrs: extra, extraSellers, cagr: cagrAt(inputs), short: false };
    }
  }
  const { base, extraSellers } = staffed(max);
  const last = { ...base, winRate: ceiling };
  return { inputs: last, extraBdrs: max, extraSellers, cagr: cagrAt(last), short: true };
}

// ---- benchmarks beside each input (quick-261007-ffh, John 2026-10-07) --------------
//
// "have the benchmarks readily available for each of the editable sales metrics".
// Distilled from the Funnel Metric Definitions & Benchmarks workbook's benchmark
// column (private B2B SaaS, $10M–$50M ARR, ACV $50k–$150k, integration/infra
// vertical). `low`/`high` is the typical band in the input's own unit;
// `better` says which direction is good so the page can colour the comparison.

export interface InputBenchmark {
  /** Typical band, in the input's unit (rates 0..1, money $, counts). */
  low: number;
  high: number;
  better: 'high' | 'low';
  /** Short text shown beside the input, e.g. "25–40% (40–50% good)". */
  text: string;
  /** Where it comes from — tooltip. */
  source: string;
}

export const MODEL3Y_BENCHMARKS: Partial<Record<keyof Model3yInputs, InputBenchmark>> = {
  rampMonths: { low: 3, high: 6, better: 'low', text: '3–6 months', source: 'Bridge Group 2025 SDR Metrics: ramp 2.8–5.8 months by ACV' },
  bdrRampMonths: { low: 3, high: 6, better: 'low', text: '3–6 months', source: 'Bridge Group 2025 SDR Metrics: ramp 2.8–5.8 months by ACV' },
  conversationsPerBdrMonth: { low: 80, high: 100, better: 'high', text: '80–100 / mo', source: 'Bridge Group 2025: 4.1–4.4 quality conversations a day per SDR ≈ 90 a month' },
  leadToBooked: { low: 0.05, high: 0.1, better: 'high', text: '5–10% (12–20% composite)', source: 'Crunchbase / RevenueHero 2025: 5–10% for a low-intent inbound mix (Adeptia’s is ~70% social, paid, syndication); composite lead→meeting 12–20% for $10M–$100M B2B SaaS' },
  inboundDeliveredOfBooked: { low: 0.75, high: 0.85, better: 'high', text: '75–85% show', source: 'Operatix / Gradient Works: 15 booked → 80% show → 12 held' },
  inboundMtgToOpp: { low: 0.25, high: 0.4, better: 'high', text: '25–40% (40–50% good)', source: 'Optifai Pipeline Study 2026 (N=939), Zeliq 2025, Oppora 2026: first meeting held → qualified opp 25–40% typical, 40–50% good, 55–60% top quartile; inbound converts 1.3–1.5× outbound' },
  conversationsPerMeeting: { low: 6, high: 7, better: 'low', text: '6–7 (top 4–5)', source: 'Bridge Group 2025: ~90 conversations and 12–15 meetings a month per SDR; top performers 20–25 meetings on the same volume' },
  outboundDeliveredOfBooked: { low: 0.75, high: 0.85, better: 'high', text: '75–85% show', source: 'Operatix / Gradient Works: 15 booked → 80% show → 12 held' },
  outboundMtgToOpp: { low: 0.2, high: 0.35, better: 'high', text: '20–35% (40%+ top)', source: 'Optifai 2026 / Oppora 2026: outbound first meeting held → qualified opp 20–35% typical, 40%+ top quartile' },
  avgCreatedOppValue: { low: 50_000, high: 150_000, better: 'high', text: '$50K–$150K ($85K median)', source: 'Optifai ACV Benchmark 2026: DevOps / data infrastructure / integration vertical median ACV $85k, range $50k–$150k' },
  winRate: { low: 0.15, high: 0.22, better: 'high', text: '15–22% (18% median)', source: 'Optifai 2026: $50k–$100k ACV win rate 15–22%, 18% median, 22%+ top quartile; Ebsta × Pavilion 2025: 19–21% overall; new-logo runs 10–15 pts below expansion' },
  avgWonValue: { low: 50_000, high: 150_000, better: 'high', text: '$50K–$150K ($85K median)', source: 'Optifai ACV Benchmark 2026: integration/infra vertical $85k median ACV, $50k–$150k; KeyBanc 2025 private SaaS 6-month-cycle cohort $62k' },
  cycleDays: { low: 60, high: 90, better: 'low', text: '60–90 days (>$100K: 90–180)', source: 'Optifai Sales Cycle 2026 (won deals, 25th–75th): $50k–$100k 60–90 days, >$100k 90–180+; overall B2B SaaS median 84 days' },
  churnPct: { low: 0.08, high: 0.12, better: 'low', text: '8–12% (GRR 88% median)', source: 'Pavilion × Benchmarkit 2025: gross revenue retention 88% median' },
};

// ---- actuals assembly ---------------------------------------------------------

/**
 * Funnel-rate baseline measured in the Funnel Metric Definitions & Benchmarks
 * workbook (Salesforce pull 2026-09-30, trailing five fiscal quarters
 * Q3-2025..Q3-2026) and the Rillet Aug-2026 ARR waterfall. These stages have no
 * live aggregate on the dashboard yet, so they ship as dated constants and are
 * editable on the page.
 *
 * Refresh routine (quick-261007-f4o): the funnel-stage figures are the Jun→Sep
 * sums of the monthly series behind the Metric Trends tab —
 * `node ~/funnel-benchmarks/trends-live.js 2026-06 <last> <pulled>` on the compact
 * pulls in ~/funnel-benchmarks/live/, then sum num/den per metric (lead→booked
 * over matured source months only). Deal economics come from
 * ~/funnel-benchmarks/plan-inputs.json (trailing 5q).
 */
export const FUNNEL_BASELINE_2026_09 = {
  pulledOn: '2026-10-30',
  /**
   * FUNNEL-STAGE RATES AND VOLUMES: the LAST SIX MONTHS, May-1 → Oct-30 2026 (quick-261030-ka4,
   * John 2026-10-30: "lets look at last 6 months"). Re-pulled 2026-10-30; sums of the
   * trends-live.js monthly series via ~/funnel-benchmarks/baseline-6mo.js 2026-05 2026-10.
   * Inbound leads and New Business opps created come from the SAME window (they used to be the
   * live trailing-90-day aggregates) so the meeting→opp trace scale reconciles inside one window.
   * DEAL ECONOMICS below stay on the trailing five fiscal quarters (small win counts).
   */
  funnelWindow: 'May–Oct 2026 (last 6 months)',
  dealWindow: 'trailing 5 fiscal quarters',
  /** Quota-carrying sellers (AEs). */
  sellers: 5,
  rampMonths: 6,
  /**
   * Conversations a month at TODAY's team and the measured per-head rates = 4 BDRs × 97 + 204.67
   * AE dials (the bdr + seller figures below). The window logged 3,362 = 560 a month (May 105 ·
   * Jun 456 · Jul 630 · Aug 443 · Sep 932 · Oct 796) because May ran with two BDRs; the trace
   * scale is solved on today's team so the actuals still reproduce the 44 New Business opps.
   */
  conversationsPerMonth: 592.67,
  /**
   * The same 3,362 split by the caller's role (calldisp-role.json): BDRs 2,134 over 22 BDR-months
   * (two BDRs in May, four since June) = 97 a BDR a month, run at today's four BDRs;
   * AEs + the sales manager 1,228 = 204.67 a month.
   */
  bdrs: 4,
  bdrConversationsPerMonth: 388,
  sellerConversationsPerMonth: 204.67,
  /** Assumption, not measured: matches the low end of the 3–6 month SDR ramp benchmark. */
  bdrRampMonths: 3,
  /**
   * AE capacity assumption: delivered new-logo meetings a fully ramped AE can work a month.
   * Today: 142 delivered (36 inbound + 106 outbound) over 6 months ÷ 5 AEs ≈ 5 a month each.
   */
  meetingsPerAeMonth: 12,
  /** Non-DQ inbound leads by source month, May–Oct: 496 ÷ 6. */
  inboundLeadsPerMonth: 82.67,
  /** M1: 33 booked / 388 non-DQ inbound leads, source months May–Aug (the ones 60+ days old at the pull). */
  leadToBooked: 0.0851,
  /** 19 delivered of the 33 booked (same matured cohort). */
  inboundDeliveredOfBooked: 0.576,
  /** M2 meeting basis: 14 opps / 36 delivered Inbound New meetings. */
  inboundMtgToOpp: 0.389,
  /** M3: 3,362 conversations / 230 Outbound New meetings booked. */
  conversationsPerMeeting: 14.6,
  /** 106 delivered / 224 come due (Outbound New). */
  outboundDeliveredOfBooked: 0.473,
  /**
   * M4: 21 opps / 106 delivered Outbound New meetings. September's 20 delivered meetings still
   * trace 0 opps a month later; October's 22 trace 5 so far (the trace allows the following month).
   */
  outboundMtgToOpp: 0.198,
  /** New Business opps created May–Oct: 44 ÷ 6 — what the actuals' meeting→opp trace scale reproduces. */
  newLogoOppsPerMonth: 7.33,
  /** New Business share of Product opps created May–Oct: 44 of 53. */
  newLogoShareOfOpps: 0.83,
  /**
   * SALES CYCLE (quick-261030-ka4, John: "cycle is way too short"): New Business Closed Won,
   * AVERAGE CreatedDate → CloseDate, trailing 5q: 9 wins (19, 23, 39, 45, 55, 65, 170, 210, 256 days)
   * = 98. Dropped: Prudential Initial Land (created after it closed) and, per John 2026-11-02, Pacific
   * Life Onboarding App feeds (006VI00000KNy82YAD, opened 2024-05-10, won 2026-03-25, 684 days —
   * one record that alone moved the average from 98 to 157). The old figure
   * was the MEDIAN of ALL won Product deals (expansion included) = 64: the median lands in the
   * fast half of a two-humped set (five small lands in 3–9 weeks, five deals in 6–23 months),
   * expansion deals close quickly, and lost deals (median 124 days) are never counted. The model
   * uses the cycle as the share of a year's pipeline that closes next year — an average, so the mean.
   */
  newLogoCycleDays: 98,
  /** M5, New Business only, trailing 5q: 11 won / 106 closed (Closed Won + Closed Lost). */
  newLogoWinRate: 0.1038,
  /** M7, New Business won deals, trailing 5q: avg of 11 (median $71,250). */
  avgNewLogoDeal: 89_005,
  /** Average created New Business opportunity Amount, trailing 5q (n=108). */
  avgNewLogoCreatedOpp: 88_389,
  /**
   * The base the model opens FY2027 on = the FY2027 OPENING ARR FORECAST, $14.9M
   * (John 2026-10-07, quick-261007-gl8: "opening arr for next year is forecast to be
   * 14.9M"). Dated forecast — replace with the Rillet year-end actual when it closes.
   * Rillet ACTIVE waterfall, Aug 2026, was $12,654,122 (rilletActiveArrAug2026).
   */
  startingArr: 14_900_000,
  startingArrBasis: 'FY2027 opening ARR forecast (2026-10-07)',
  rilletActiveArrAug2026: 12_654_122,
  /** Rillet: expansion from current customers in the trailing year, $1.58M on a $10.75M base. */
  expansionHistoricalArr: 1_580_000,
  /** The same as a rate: 14.7% (5.9% / 21.3% / 14.7% over the last three years). */
  expansionHistoricalPct: 0.147,
  /** Rillet: $492k churn + $70k contraction on a $10.75M base. */
  churnPct: 0.046,
  /**
   * Measured demand growth: inbound leads by Latest_Source_Date__c ran +7% H1-2026 vs
   * H2-2025 but −8% Jan–Sep 2026 vs Jan–Sep 2025 — no trend, so actuals assume 0.
   * (Booked meetings look like +190% but that is Meeting_Type__c adoption from Sep-2025,
   * not demand.)
   */
  inboundGrowthPct: 0,
  sellersAddedPerYear: 0,
  conversionImprovementPct: 0,
} as const;

export interface Model3yLiveBaseline {
  /** All-Product win rate (MAIN-24) — context only; the model runs on the New Business rate. */
  winRate: number;
  /** All-Product average won deal (MAIN-12) — context only; the model runs on the New Business deal. */
  avgWonValue: number;
  /** All-Product average created opp — context only. */
  avgCreatedOppValue: number;
  inboundLeadsPerQuarter: number;
  /** All-Product opps created (trailing 90d); the model takes the New Business share. */
  oppsCreatedPerQuarter: number;
  /** Opportunity creation → close, days (median). */
  oppToWinDays: number;
  /** Default target: new-logo ARR per year ($). */
  targetNewArrPerYear: number;
  /** Open New Business pipeline ($) close-dated in the first plan year (quick-261008-mi0). */
  openPlanYearPipeline: number;
  /** Open New Business pipeline ($) close-dated in Q4 of the prior FY at Best Case + Pipeline (quick-261008-mi0). */
  openPriorQ4RollForwardPipeline: number;
}

/**
 * Every new-logo opportunity is meeting-sourced (John 2026-09-28). The workbook's
 * account-trace links only part of the New Business opps to a tagged
 * Inbound New / Outbound New meeting, so the traced meeting→opp rates are scaled
 * up until the funnel reproduces the live New Business opp-creation run-rate.
 * Returned so the page can show the scale next to the rates.
 */
export interface ActualsMeta {
  /** Multiplier applied to both traced meeting→opp rates (1 = no scaling). */
  mtgToOppScale: number;
  /** Live New Business opps created per year (opps created × NB share × 4). */
  nbOppsPerYear: number;
  /** Opps per year the traced workbook rates alone would produce. */
  tracedOppsPerYear: number;
}

export function buildActualsMeta(live: Model3yLiveBaseline, fb: typeof FUNNEL_BASELINE_2026_09 = FUNNEL_BASELINE_2026_09): ActualsMeta {
  const inboundLeadsPerMonth = fb.inboundLeadsPerMonth;
  const inboundOppsPerMonth = inboundLeadsPerMonth * fb.leadToBooked * fb.inboundDeliveredOfBooked * fb.inboundMtgToOpp;
  const outboundOppsPerMonth = (fb.conversationsPerMonth / fb.conversationsPerMeeting) * fb.outboundDeliveredOfBooked * fb.outboundMtgToOpp;
  const traced = (inboundOppsPerMonth + outboundOppsPerMonth) * 12;
  // quick-261030-ka4: New Business opps created in the SAME six months as the rates
  const nb = fb.newLogoOppsPerMonth * 12;
  const scale = traced > 0 && nb > 0 ? nb / traced : 1;
  return { mtgToOppScale: scale, nbOppsPerYear: nb, tracedOppsPerYear: traced };
}

/**
 * Build the "actuals" input set — NEW LOGO ONLY: live dashboard aggregates where
 * the metric has one (inbound leads, opps created × New Business share, cycle),
 * the dated workbook baseline for the rest (funnel-stage rates, New Business win
 * rate and deal sizes, Rillet base). Meeting→opp rates carry the trace scale so
 * the actuals reproduce today's New Business opportunity-creation run-rate.
 */
export function buildActuals(live: Model3yLiveBaseline, fb: typeof FUNNEL_BASELINE_2026_09 = FUNNEL_BASELINE_2026_09): Model3yInputs {
  const inboundLeadsPerMonth = fb.inboundLeadsPerMonth;
  const { mtgToOppScale } = buildActualsMeta(live, fb);
  return {
    sellers: fb.sellers,
    baseSellers: fb.sellers,
    rampMonths: fb.rampMonths,
    conversationsPerSellerMonth: fb.sellerConversationsPerMonth / fb.sellers,
    bdrs: fb.bdrs,
    baseBdrs: fb.bdrs,
    bdrRampMonths: fb.bdrRampMonths,
    conversationsPerBdrMonth: fb.bdrConversationsPerMonth / fb.bdrs,
    bdrsAddedPerYear: 0,
    meetingsPerAeMonth: fb.meetingsPerAeMonth,
    inboundLeadsPerMonth,
    leadToBooked: fb.leadToBooked,
    inboundDeliveredOfBooked: fb.inboundDeliveredOfBooked,
    inboundMtgToOpp: clamp01(fb.inboundMtgToOpp * mtgToOppScale),
    conversationsPerMeeting: fb.conversationsPerMeeting,
    outboundDeliveredOfBooked: fb.outboundDeliveredOfBooked,
    outboundMtgToOpp: clamp01(fb.outboundMtgToOpp * mtgToOppScale),
    avgCreatedOppValue: fb.avgNewLogoCreatedOpp,
    winRate: fb.newLogoWinRate,
    avgWonValue: fb.avgNewLogoDeal,
    cycleDays: fb.newLogoCycleDays,
    startingArr: fb.startingArr,
    expansionArrPerYear: fb.expansionHistoricalArr,
    churnPct: fb.churnPct,
    annualDrift: 0,
    inboundGrowthPct: fb.inboundGrowthPct,
    sellersAddedPerYear: fb.sellersAddedPerYear,
    conversionImprovementPct: fb.conversionImprovementPct,
    targetNewArrPerYear: Math.max(0, live.targetNewArrPerYear),
    pipelineInHandOpen: Math.max(0, live.openPlanYearPipeline),
    pipelineInHandPriorQ4: Math.max(0, live.openPriorQ4RollForwardPipeline),
    priorQ4RollForwardPct: PRIOR_Q4_ROLL_FORWARD_PCT,
  };
}

// ---- lever options: doable or not (John 2026-09-28) ----------------------------

/**
 * Doability thresholds, one line each, with the benchmark behind it:
 *  - hires: one hiring class = up to 3 added sellers on top of the modeled team
 *  - win rate: 22% = top quartile for $50k–$100k ACV (Optifai 2026), the workbook's benchmark ceiling
 *  - meeting→opp: 42% = mid-market meeting→proposal (Optifai 2026)
 *  - deal size: +25% over the modeled average and at most $150k (the ACV band the benchmarks cover)
 *  - inbound: +50% over the modeled volume within a year
 *  - outbound efficiency: no better than 7 conversations per booked meeting (Bridge Group)
 */
export const LEVER_LIMITS = {
  maxAddedSellers: 3,
  winRateCeiling: 0.22,
  mtgToOppCeiling: 0.42,
  dealUpliftMax: 0.25,
  dealCeiling: 150_000,
  inboundGrowthMax: 0.5,
  conversationsPerMeetingFloor: 7,
} as const;

export interface LeverOption {
  /** What has to be true, e.g. "Hire to 8 sellers (+3)" / "Win rate to 18.2%". */
  label: string;
  doable: boolean;
  /** One short reason against the threshold. */
  why: string;
}

export interface LeverOptions {
  onTarget: boolean;
  single: LeverOption[];
  multi: LeverOption[];
}

const pctS = (v: number): string => `${(v * 100).toFixed(1)}%`;
const moneyS = (v: number): string => (v >= 1_000_000 ? `$${(v / 1_000_000).toFixed(2)}M` : `$${String(Math.round(v / 1_000))}K`);
const n1 = (v: number): string => (Math.round(v * 10) / 10).toFixed(1).replace(/\.0$/, '');

/** Reference-year (first plan year) figures for a modified input set. */
function refYear(inp: Model3yInputs, carryIn: number | undefined): Model3yYear {
  // eslint-disable-next-line @typescript-eslint/non-nullable-type-assertion-style -- runModel always builds 3 years; the rule's `!` fix is rejected by no-non-null-assertion in src
  return runModel(inp, carryIn).years[0] as Model3yYear;
}

/**
 * What it takes to close the gap between the reference-year new-logo ARR and the
 * target — first one lever at a time, then combinations — each marked doable or
 * not against LEVER_LIMITS. Empty lists when already on target.
 */
export function leverOptions(inp: Model3yInputs, result: Model3yResult, carryIn?: number): LeverOptions {
  const s = result.steady;
  // The levers close the gap the FUNNEL has to close: the target net of what the
  // pipeline already in hand is expected to yield at the modeled rates (quick-261008-mi0).
  const inHandOpps = safeDiv(pipelineInHand(inp).total, Math.max(0, inp.avgCreatedOppValue));
  const target = Math.max(0, inp.targetNewArrPerYear - inHandOpps * s.winRate * s.avgWonValue);
  const onTarget = target <= 0 || s.production >= target * 0.98;
  if (onTarget) return { onTarget: true, single: [], multi: [] };
  const L = LEVER_LIMITS;
  const reqWins = safeDiv(target, s.avgWonValue);
  const winSafe = Math.max(1e-9, s.winRate);

  // --- single levers ---
  const single: LeverOption[] = [];
  // 1. hires (quick-261029-fkq): BDRs fill the funnel, AEs work the meetings it produces
  const bdr = bdrsToTarget(inp, carryIn, target);
  single.push({
    label: Number.isFinite(bdr.bdrs)
      ? `Hire to ${String(bdr.bdrs)} BDRs (+${String(bdr.bdrs - inp.bdrs)})${bdr.aes > inp.sellers ? ` and ${String(bdr.aes)} AEs (+${String(bdr.aes - inp.sellers)}) to take the meetings` : ''} at today’s productivity`
      : 'Hire BDRs your way there',
    doable: Number.isFinite(bdr.bdrs) && bdr.bdrs - inp.bdrs <= L.maxAddedSellers && bdr.aes - inp.sellers <= L.maxAddedSellers,
    why: Number.isFinite(bdr.bdrs) ? `one hiring class is up to +${String(L.maxAddedSellers)} per role` : 'outbound produces nothing per BDR at these rates',
  });
  // 2. win rate
  const winNeeded = safeDiv(reqWins, s.opps);
  single.push({ label: `Win rate to ${pctS(winNeeded)} (from ${pctS(s.winRate)})`, doable: winNeeded <= L.winRateCeiling, why: `benchmark ceiling ${pctS(L.winRateCeiling)}` });
  // 3. deal size
  const dealNeeded = safeDiv(target, s.opps * winSafe);
  single.push({
    label: `Average deal to ${moneyS(dealNeeded)} (from ${moneyS(s.avgWonValue)})`,
    doable: dealNeeded <= s.avgWonValue * (1 + L.dealUpliftMax) && dealNeeded <= L.dealCeiling,
    why: `up to +${String(Math.round(L.dealUpliftMax * 100))}% and ${moneyS(L.dealCeiling)}`,
  });
  // 4. meeting→opp, pooled (both channels move together)
  const delivered = s.inboundDelivered + s.outboundDelivered;
  const m2oNeeded = safeDiv(reqWins / winSafe, delivered);
  single.push({
    label: `Meeting → opp to ${pctS(m2oNeeded)} pooled (from ${pctS(safeDiv(s.opps, delivered))})`,
    doable: m2oNeeded <= L.mtgToOppCeiling,
    why: `benchmark ceiling ${pctS(L.mtgToOppCeiling)}`,
  });
  // 5. inbound volume
  const inboundOppsNeeded = reqWins / winSafe - s.outboundOpps;
  const inboundMult = safeDiv(inboundOppsNeeded, s.inboundOpps);
  single.push({
    label:
      inboundMult > 0
        ? `Inbound leads ×${n1(inboundMult)} (${String(Math.round(s.inboundLeads * inboundMult))} / yr from ${String(Math.round(s.inboundLeads))})`
        : 'Inbound leads: outbound alone already covers it',
    doable: inboundMult > 0 && inboundMult <= 1 + L.inboundGrowthMax,
    why: `up to +${String(Math.round(L.inboundGrowthMax * 100))}% in a year`,
  });
  // 6. outbound efficiency
  const outboundOppsNeeded = reqWins / winSafe - s.inboundOpps;
  const obBookedNeeded = safeDiv(outboundOppsNeeded, inp.outboundDeliveredOfBooked * inp.outboundMtgToOpp);
  const cpmNeeded = safeDiv(s.conversations, obBookedNeeded);
  single.push({
    label:
      outboundOppsNeeded > 0
        ? `Conversations per meeting down to ${n1(cpmNeeded)} (from ${n1(inp.conversationsPerMeeting)})`
        : 'Outbound efficiency: inbound alone already covers it',
    doable: outboundOppsNeeded > 0 && cpmNeeded >= L.conversationsPerMeetingFloor,
    why: `benchmark floor ${String(L.conversationsPerMeetingFloor)}`,
  });

  // --- multi levers: fix the cheap levers, solve the last one ---
  const multi: LeverOption[] = [];
  const combo = (label: string, mod: Partial<Model3yInputs>, solve: 'winRate' | 'bdrs' | 'deal'): void => {
    const y = refYear({ ...inp, ...mod }, carryIn);
    if (solve === 'winRate') {
      const w = safeDiv(reqWins, y.opps);
      multi.push({ label: `${label}, then win rate to ${pctS(w)}`, doable: w <= L.winRateCeiling, why: `benchmark ceiling ${pctS(L.winRateCeiling)}` });
    } else if (solve === 'deal') {
      const d = safeDiv(target, y.opps * Math.max(1e-9, y.winRate));
      multi.push({
        label: `${label}, then average deal to ${moneyS(d)}`,
        doable: d <= y.avgWonValue * (1 + L.dealUpliftMax) && d <= L.dealCeiling,
        why: `up to +${String(Math.round(L.dealUpliftMax * 100))}% and ${moneyS(L.dealCeiling)}`,
      });
    } else {
      const h = bdrsToTarget({ ...inp, ...mod }, carryIn, target);
      const baseB = mod.bdrs ?? inp.bdrs;
      const baseA = mod.sellers ?? inp.sellers;
      const aeText = h.aes > baseA ? ` and ${String(h.aes)} AEs (+${String(h.aes - baseA)})` : '';
      multi.push({
        label: Number.isFinite(h.bdrs) ? `${label}, then hire to ${String(h.bdrs)} BDRs (+${String(h.bdrs - baseB)})${aeText}` : `${label}, then hire BDRs`,
        doable: Number.isFinite(h.bdrs) && h.bdrs - baseB <= L.maxAddedSellers && h.aes - baseA <= L.maxAddedSellers,
        why: `one hiring class is up to +${String(L.maxAddedSellers)} per role`,
      });
    }
  };
  const cpmBench = Math.min(inp.conversationsPerMeeting, L.conversationsPerMeetingFloor);
  combo('+2 BDRs', { bdrs: inp.bdrs + 2 }, 'winRate');
  combo(`Outbound at benchmark (${String(L.conversationsPerMeetingFloor)} conversations per meeting)`, { conversationsPerMeeting: cpmBench }, 'winRate');
  combo(
    'Inbound +25% and meeting → opp +5 pts',
    { inboundLeadsPerMonth: inp.inboundLeadsPerMonth * 1.25, inboundMtgToOpp: clamp01(inp.inboundMtgToOpp + 0.05), outboundMtgToOpp: clamp01(inp.outboundMtgToOpp + 0.05) },
    'winRate',
  );
  combo('+2 BDRs and outbound at benchmark', { bdrs: inp.bdrs + 2, conversationsPerMeeting: cpmBench }, 'deal');
  combo('Win rate +5 pts and outbound at benchmark', { winRate: clamp01(inp.winRate + 0.05), conversationsPerMeeting: cpmBench }, 'bdrs');

  return { onTarget: false, single, multi };
}

/** Upper bound on the BDR search; past this the lever is reported as not reachable. */
const MAX_BDRS_SEARCH = 60;

/**
 * Whole BDRs (fully ramped, as the reference year is judged) needed for the first plan year
 * to produce `target` new-logo ARR, with enough AEs that no delivered meeting goes unworked.
 * Infinity when even MAX_BDRS_SEARCH BDRs cannot get there.
 */
function bdrsToTarget(inp: Model3yInputs, carryIn: number | undefined, target: number): { bdrs: number; aes: number } {
  const at = (bdrs: number, sellers: number): Model3yYear =>
    refYear({ ...inp, bdrs, baseBdrs: bdrs, bdrRampMonths: 0, sellers, baseSellers: sellers, rampMonths: 0 }, carryIn);
  // AEs that take every delivered meeting at a given BDR count (never fewer than today's)
  const aesFor = (bdrs: number): number => {
    const y = at(bdrs, inp.sellers);
    const held = y.inboundDelivered + y.outboundDelivered + y.meetingsUnworked;
    return Math.max(inp.sellers, Math.ceil(safeDiv(held, Math.max(0, inp.meetingsPerAeMonth) * 12) - 1e-9));
  };
  for (let b = Math.max(0, Math.ceil(inp.bdrs)); b <= MAX_BDRS_SEARCH; b++) {
    const aes = aesFor(b);
    if (at(b, aes).production >= target * 0.98) return { bdrs: b, aes };
  }
  return { bdrs: Infinity, aes: inp.sellers };
}

// ---- target walk (quick-261030-ka4, John 2026-10-30) -------------------------------
//
// "we jump to needed but we dont call out what our actuals are producing as a constant …
// if a target is 2.1m, and the actual win rate and actual avg deal size gets us to a number …
// what type of activity is needed … compare that activity … to what our current actuals
// activity rates are. and show the gap … then … show a column of what an improved funnel
// produces … the assumptions we made … beside the area or row they impact."
//
// One table, read bottom-up the way the gut check runs: target → deal size → win rate →
// wins → opps → meetings → activity → headcount. Columns:
//   run rate  — the last-six-month actuals run for a year. Built from the actuals only, so
//               no scenario edit moves it.
//   needed    — the target walked back through TODAY's rates (deal size, win rate,
//               meeting→opp, show rates, conversations per meeting, lead→booked), split
//               inbound/outbound like today, net of the pipeline already in hand. Rate rows
//               are held, so they carry no gap. Each value says how it was derived.
//   gap       — needed − run rate on the volume rows.
//   improved  — the scenario's first plan year (the preset or the user's edits).
// Every input the scenario changes from actuals is listed beside the rows it moves.

export type WalkUnit = 'money' | 'count' | 'pct' | 'ratio';
export type WalkGroup = 'Outcome' | 'Opportunities' | 'Meetings' | 'Activity' | 'Headcount';

export interface WalkRow {
  key: string;
  group: WalkGroup;
  label: string;
  unit: WalkUnit;
  /** Last-six-month actuals run for a year — constant, never moves with the scenario. */
  runRate: number;
  /** What the target needs at today's rates (a rate row: the rate, held). */
  needed: number;
  /** The scenario's first plan year. */
  improved: number;
  /** A rate row: held at actuals in the needed column, so it has no gap. */
  rate: boolean;
  /** For a rate row: true when a LOWER value is better (conversations per meeting). */
  lowerIsBetter?: boolean;
  /** How the needed value is derived. */
  derivation: string;
  /** Scenario changes from actuals that move this row, e.g. "Win rate 10.4% → 21.3%". */
  assumptions: string[];
}

export interface WalkAssumption {
  input: keyof Model3yInputs;
  /** "Win rate 10.4% → 21.3% (benchmark 15–22% (18% median))". */
  text: string;
  /** Labels of the walk rows it moves; empty for year-2/3 levers and timing. */
  rows: string[];
  /** Only moves the later plan years (growth levers) or when ARR lands (cycle). */
  outsideFirstYear: boolean;
}

export interface TargetWalk {
  target: number;
  rows: WalkRow[];
  assumptions: WalkAssumption[];
  /** Today's inbound share of funnel opps — the split the needed column keeps. */
  inboundShare: number;
}

type AssumptionFmt = 'pct' | 'money' | 'num' | 'int' | 'days' | 'months';

/** Which walk rows each scenario input moves (empty = later years / timing only). */
const WALK_INPUTS: { input: keyof Model3yInputs; label: string; fmt: AssumptionFmt; rows: string[]; outside?: string }[] = [
  { input: 'avgWonValue', label: 'Average won deal', fmt: 'money', rows: ['avgWon', 'wins', 'newArr'] },
  { input: 'winRate', label: 'Win rate', fmt: 'pct', rows: ['winRate', 'wins', 'newArr'] },
  { input: 'avgCreatedOppValue', label: 'Average created opp', fmt: 'money', rows: ['pipeline', 'inHand'] },
  { input: 'inboundMtgToOpp', label: 'Inbound meeting → opp', fmt: 'pct', rows: ['inM2O', 'opps'] },
  { input: 'outboundMtgToOpp', label: 'Outbound meeting → opp', fmt: 'pct', rows: ['obM2O', 'opps'] },
  { input: 'inboundDeliveredOfBooked', label: 'Inbound show rate', fmt: 'pct', rows: ['inShow', 'inDel'] },
  { input: 'outboundDeliveredOfBooked', label: 'Outbound show rate', fmt: 'pct', rows: ['obShow', 'obDel'] },
  { input: 'leadToBooked', label: 'Lead → booked', fmt: 'pct', rows: ['l2b', 'inBooked'] },
  { input: 'inboundLeadsPerMonth', label: 'Inbound leads / month', fmt: 'num', rows: ['leads'] },
  { input: 'conversationsPerMeeting', label: 'Conversations per booked meeting', fmt: 'num', rows: ['cpm', 'obBooked'] },
  { input: 'conversationsPerBdrMonth', label: 'Conversations per BDR / month', fmt: 'num', rows: ['convBdr', 'conversations'] },
  { input: 'conversationsPerSellerMonth', label: 'Conversations per AE / month', fmt: 'num', rows: ['conversations'] },
  { input: 'bdrs', label: 'BDRs', fmt: 'int', rows: ['bdrs', 'conversations'] },
  { input: 'bdrRampMonths', label: 'New-BDR ramp', fmt: 'months', rows: ['bdrs'] },
  { input: 'sellers', label: 'AEs', fmt: 'int', rows: ['aes', 'conversations'] },
  { input: 'rampMonths', label: 'New-AE ramp', fmt: 'months', rows: ['aes'] },
  { input: 'meetingsPerAeMonth', label: 'Meetings an AE can work / month', fmt: 'num', rows: ['aeCap', 'inDel', 'obDel'] },
  { input: 'cycleDays', label: 'Sales cycle', fmt: 'days', rows: [], outside: 'when the ARR lands, not how much' },
  { input: 'bdrsAddedPerYear', label: 'BDRs added each later year', fmt: 'int', rows: [], outside: 'years 2–3' },
  { input: 'sellersAddedPerYear', label: 'AEs added each later year', fmt: 'int', rows: [], outside: 'years 2–3' },
  { input: 'inboundGrowthPct', label: 'Inbound growth / year', fmt: 'pct', rows: [], outside: 'years 2–3' },
  { input: 'conversionImprovementPct', label: 'Conversion improvement / year', fmt: 'pct', rows: [], outside: 'years 2–3' },
  { input: 'annualDrift', label: 'Win rate and deal size drift / year', fmt: 'pct', rows: [], outside: 'years 2–3' },
];

function fmtAssumption(v: number, f: AssumptionFmt): string {
  switch (f) {
    case 'pct':
      return pctS(v);
    case 'money':
      return moneyS(v);
    case 'int':
      return String(Math.round(v));
    case 'days':
      return `${String(Math.round(v))} days`;
    case 'months':
      return `${n1(v)} mo`;
    default:
      return n1(v);
  }
}

const cnt = (v: number): string => Math.round(v).toLocaleString('en-US');

/**
 * The target walk. `actuals` = buildActuals(...) (the run-rate basis); `inputs` = the
 * scenario. The target and the pipeline in hand are read from `inputs` for every column —
 * they are facts or goals, not funnel assumptions.
 */
export function targetWalk(actuals: Model3yInputs, inputs: Model3yInputs, carryIn?: number): TargetWalk {
  const base: Model3yInputs = {
    ...actuals,
    targetNewArrPerYear: inputs.targetNewArrPerYear,
    pipelineInHandOpen: inputs.pipelineInHandOpen,
    pipelineInHandPriorQ4: inputs.pipelineInHandPriorQ4,
    priorQ4RollForwardPct: inputs.priorQ4RollForwardPct,
  };
  const baseRun = runModel(base);
  const a = baseRun.steady;
  const need = requiredChain(base, baseRun);
  const scen = runModel(inputs, carryIn);
  const s = scen.steady;
  const sreq = requiredChain(inputs, scen);
  const req = (key: string): number => need.stages.find((x) => x.key === key)?.required ?? 0;

  const target = need.target;
  const reqWins = safeDiv(target, a.avgWonValue);
  const reqOppsTotal = safeDiv(reqWins, a.winRate);
  const inHandA = need.pipelineInHandOpps;
  const inHandS = sreq.pipelineInHandOpps;
  const funnelOpps = a.inboundOpps + a.outboundOpps;
  const inboundShare = funnelOpps > 0 ? a.inboundOpps / funnelOpps : 0.5;
  const reqOpps = req('opps');
  const reqInOpps = reqOpps * inboundShare;
  const reqObOpps = reqOpps * (1 - inboundShare);
  const reqInDel = req('inDelivered');
  const reqObDel = req('obDelivered');
  const reqInBooked = req('inBooked');
  const reqObBooked = req('obBooked');
  const reqConv = req('conversations');
  const held = (v: string): string => `held at today's ${v}`;

  const rows: WalkRow[] = [
    // Outcome
    {
      key: 'newArr', group: 'Outcome', label: 'New-logo ARR (funnel + pipeline in hand)', unit: 'money', rate: false,
      runRate: a.production + need.pipelineInHandExpectedArr, needed: target, improved: s.production + sreq.pipelineInHandExpectedArr,
      derivation: 'the target', assumptions: [],
    },
    {
      key: 'avgWon', group: 'Outcome', label: 'Average won deal', unit: 'money', rate: true,
      runRate: a.avgWonValue, needed: a.avgWonValue, improved: s.avgWonValue, derivation: held(moneyS(a.avgWonValue)), assumptions: [],
    },
    {
      key: 'winRate', group: 'Outcome', label: 'Win rate', unit: 'pct', rate: true,
      runRate: a.winRate, needed: a.winRate, improved: s.winRate, derivation: held(pctS(a.winRate)), assumptions: [],
    },
    {
      key: 'wins', group: 'Outcome', label: 'Wins', unit: 'count', rate: false,
      runRate: a.wins + inHandA * a.winRate, needed: reqWins, improved: s.wins + inHandS * s.winRate,
      derivation: `${moneyS(target)} ÷ ${moneyS(a.avgWonValue)} = ${n1(reqWins)}`, assumptions: [],
    },
    // Opportunities
    {
      key: 'oppsTotal', group: 'Opportunities', label: 'Opportunities worked (in hand + created)', unit: 'count', rate: false,
      runRate: inHandA + a.opps, needed: reqOppsTotal, improved: inHandS + s.opps,
      derivation: `${n1(reqWins)} wins ÷ ${pctS(a.winRate)} = ${cnt(reqOppsTotal)}`, assumptions: [],
    },
    {
      key: 'inHand', group: 'Opportunities', label: 'Already in hand (open pipeline)', unit: 'count', rate: false,
      runRate: inHandA, needed: inHandA, improved: inHandS,
      derivation: `${moneyS(need.pipelineInHand)} open ÷ ${moneyS(base.avgCreatedOppValue)} an opp = ${cnt(inHandA)}`, assumptions: [],
    },
    {
      key: 'opps', group: 'Opportunities', label: 'Opportunities to create', unit: 'count', rate: false,
      runRate: a.opps, needed: reqOpps, improved: s.opps,
      derivation: `${cnt(reqOppsTotal)} − ${cnt(inHandA)} in hand = ${cnt(reqOpps)}`, assumptions: [],
    },
    {
      key: 'pipeline', group: 'Opportunities', label: 'Pipeline to create', unit: 'money', rate: false,
      runRate: a.pipelineCreated, needed: need.pipelineToGenerate, improved: s.pipelineCreated,
      derivation: `${cnt(reqOpps)} × ${moneyS(base.avgCreatedOppValue)}`, assumptions: [],
    },
    // Meetings
    {
      key: 'inM2O', group: 'Meetings', label: 'Inbound meeting → opp', unit: 'pct', rate: true,
      runRate: base.inboundMtgToOpp, needed: base.inboundMtgToOpp, improved: inputs.inboundMtgToOpp, derivation: held(pctS(base.inboundMtgToOpp)), assumptions: [],
    },
    {
      key: 'inDel', group: 'Meetings', label: 'Inbound meetings delivered', unit: 'count', rate: false,
      runRate: a.inboundDelivered, needed: reqInDel, improved: s.inboundDelivered,
      derivation: `${cnt(reqOpps)} opps × ${pctS(inboundShare)} inbound (today's mix) = ${cnt(reqInOpps)} ÷ ${pctS(base.inboundMtgToOpp)}`, assumptions: [],
    },
    {
      key: 'inShow', group: 'Meetings', label: 'Inbound show rate', unit: 'pct', rate: true,
      runRate: base.inboundDeliveredOfBooked, needed: base.inboundDeliveredOfBooked, improved: inputs.inboundDeliveredOfBooked, derivation: held(pctS(base.inboundDeliveredOfBooked)), assumptions: [],
    },
    {
      key: 'inBooked', group: 'Meetings', label: 'Inbound meetings booked', unit: 'count', rate: false,
      runRate: a.inboundBooked, needed: reqInBooked, improved: s.inboundBooked,
      derivation: `${cnt(reqInDel)} ÷ ${pctS(base.inboundDeliveredOfBooked)} show`, assumptions: [],
    },
    {
      key: 'obM2O', group: 'Meetings', label: 'Outbound meeting → opp', unit: 'pct', rate: true,
      runRate: base.outboundMtgToOpp, needed: base.outboundMtgToOpp, improved: inputs.outboundMtgToOpp, derivation: held(pctS(base.outboundMtgToOpp)), assumptions: [],
    },
    {
      key: 'obDel', group: 'Meetings', label: 'Outbound meetings delivered', unit: 'count', rate: false,
      runRate: a.outboundDelivered, needed: reqObDel, improved: s.outboundDelivered,
      derivation: `${cnt(reqOpps)} opps × ${pctS(1 - inboundShare)} outbound = ${cnt(reqObOpps)} ÷ ${pctS(base.outboundMtgToOpp)}`, assumptions: [],
    },
    {
      key: 'obShow', group: 'Meetings', label: 'Outbound show rate', unit: 'pct', rate: true,
      runRate: base.outboundDeliveredOfBooked, needed: base.outboundDeliveredOfBooked, improved: inputs.outboundDeliveredOfBooked, derivation: held(pctS(base.outboundDeliveredOfBooked)), assumptions: [],
    },
    {
      key: 'obBooked', group: 'Meetings', label: 'Outbound meetings booked', unit: 'count', rate: false,
      runRate: a.outboundBooked, needed: reqObBooked, improved: s.outboundBooked,
      derivation: `${cnt(reqObDel)} ÷ ${pctS(base.outboundDeliveredOfBooked)} show`, assumptions: [],
    },
    // Activity
    {
      key: 'l2b', group: 'Activity', label: 'Inbound lead → booked meeting', unit: 'pct', rate: true,
      runRate: base.leadToBooked, needed: base.leadToBooked, improved: inputs.leadToBooked, derivation: held(pctS(base.leadToBooked)), assumptions: [],
    },
    {
      key: 'leads', group: 'Activity', label: 'Inbound leads', unit: 'count', rate: false,
      runRate: a.inboundLeads, needed: req('leads'), improved: s.inboundLeads,
      derivation: `${cnt(reqInBooked)} booked ÷ ${pctS(base.leadToBooked)}`, assumptions: [],
    },
    {
      key: 'cpm', group: 'Activity', label: 'Conversations per booked meeting', unit: 'ratio', rate: true, lowerIsBetter: true,
      runRate: base.conversationsPerMeeting, needed: base.conversationsPerMeeting, improved: inputs.conversationsPerMeeting, derivation: held(n1(base.conversationsPerMeeting)), assumptions: [],
    },
    {
      key: 'conversations', group: 'Activity', label: 'Outbound conversations', unit: 'count', rate: false,
      runRate: a.conversations, needed: reqConv, improved: s.conversations,
      derivation: `${cnt(reqObBooked)} booked × ${n1(base.conversationsPerMeeting)}`, assumptions: [],
    },
    // Headcount
    {
      key: 'convBdr', group: 'Headcount', label: 'Conversations per BDR / month', unit: 'ratio', rate: true,
      runRate: base.conversationsPerBdrMonth, needed: base.conversationsPerBdrMonth, improved: inputs.conversationsPerBdrMonth, derivation: held(n1(base.conversationsPerBdrMonth)), assumptions: [],
    },
    {
      key: 'bdrs', group: 'Headcount', label: 'BDRs (ramp-adjusted)', unit: 'ratio', rate: false,
      runRate: a.effectiveBdrs, needed: need.bdrsNeeded, improved: s.effectiveBdrs,
      derivation: `(${cnt(reqConv)} − ${cnt(a.sellerConversations)} AE dials) ÷ (${n1(base.conversationsPerBdrMonth)} × 12)`, assumptions: [],
    },
    {
      key: 'aeCap', group: 'Headcount', label: 'Delivered meetings an AE can work / month', unit: 'ratio', rate: true,
      runRate: base.meetingsPerAeMonth, needed: base.meetingsPerAeMonth, improved: inputs.meetingsPerAeMonth, derivation: `${n1(base.meetingsPerAeMonth)} (assumption; today's AEs work ${n1(safeDiv(a.inboundDelivered + a.outboundDelivered, a.effectiveSellers * 12))})`, assumptions: [],
    },
    {
      key: 'aes', group: 'Headcount', label: 'AEs (ramp-adjusted)', unit: 'ratio', rate: false,
      runRate: a.effectiveSellers, needed: need.aesNeeded, improved: s.effectiveSellers,
      derivation: `(${cnt(reqInDel)} + ${cnt(reqObDel)} delivered) ÷ (${n1(base.meetingsPerAeMonth)} × 12)`, assumptions: [],
    },
  ];

  const byKey = new Map(rows.map((r) => [r.key, r]));
  const assumptions: WalkAssumption[] = [];
  for (const m of WALK_INPUTS) {
    const was = actuals[m.input];
    const now = inputs[m.input];
    if (Math.abs(now - was) <= 1e-9 * Math.max(1, Math.abs(was))) continue;
    const bench = MODEL3Y_BENCHMARKS[m.input];
    const text = `${m.label} ${fmtAssumption(was, m.fmt)} → ${fmtAssumption(now, m.fmt)}${bench ? ` (benchmark ${bench.text})` : ''}`;
    for (const k of m.rows) byKey.get(k)?.assumptions.push(`${m.label} ${fmtAssumption(was, m.fmt)} → ${fmtAssumption(now, m.fmt)}`);
    assumptions.push({
      input: m.input,
      text: m.outside ? `${text} — ${m.outside}` : text,
      rows: m.rows.map((k) => byKey.get(k)?.label ?? k),
      outsideFirstYear: m.rows.length === 0,
    });
  }
  return { target, rows, assumptions, inboundShare };
}
